import {
  NOT_SYMBOL,
  DELIMITER,
  RANGE_DELIMITER,
} from '@web-platform/shared-universal-constants/src/queryParams';

import { logError } from '../logger';

import type { Nullable, Maybe } from '@web-platform/shared-universal-types-utils/src/utils';

const notSymbolRegex = new RegExp(NOT_SYMBOL, 'g');
const notSymbolReplace = `%${NOT_SYMBOL.charCodeAt(0).toString(16)}`;

function fullEncode(input: string) {
  return encodeURIComponent(input).replace(notSymbolRegex, notSymbolReplace);
}

type QParamParser<ParsedValue, RawValue> = {
  serialize: (arg0: ParsedValue) => RawValue;
  deserialize: (arg0: RawValue) => ParsedValue;
};

const parseBooleanKeyValue = (
  token: string,
): Nullable<{
  key: string;
  value: boolean;
}> => {
  const key = token.substring(token[0] === NOT_SYMBOL ? 1 : 0);

  try {
    return {
      key: key && decodeURIComponent(key),
      value: token[0] !== NOT_SYMBOL,
    };
  } catch (error) {
    logError({
      error,
      component: 'queryParams',
      message: 'decodeUriComponent failed.',
    });

    return null;
  }
};

const serializeBooleanKeyValuesQueryParam = (
  arr: Array<{
    key: string;
    value: boolean;
  }>,
) =>
  arr
    .map((item) => (item.value ? fullEncode(item.key) : `${NOT_SYMBOL}${fullEncode(item.key)}`))
    .join(DELIMITER);

const deserializeBooleanKeyValues = (qp: string | null | undefined) => {
  let decodedQP: string;
  try {
    decodedQP = decodeURIComponent(qp || '');
  } catch {
    return [];
  }
  const delimitedTokens = decodedQP ? decodedQP.split(DELIMITER) : [];
  return delimitedTokens.map(parseBooleanKeyValue).filter(
    (
      x,
    ): x is {
      key: string;
      value: boolean;
    } => !!x,
  );
};

const serializeIntegerRanges = (
  ranges: Array<
    | {
        start: number;
        end: number;
      }
    | null
    | undefined
  >,
) => {
  const output: String[] = [];

  for (let i = 0; i < ranges.length; i += 1) {
    const range = ranges[i];

    if (range && /^\d+$/.test(range.start.toString()) && /^\d+$/.test(range.end.toString())) {
      output[i] = `${range.start}${RANGE_DELIMITER}${range.end}`;
    } else {
      output[i] = RANGE_DELIMITER;
    }
  }

  return output.join(DELIMITER);
};

const deserializeIntegerRanges = (qp: string | null | undefined) => {
  if (!qp) return [];
  const decodedQp = decodeURIComponent(qp);
  const ranges = decodedQp.split(DELIMITER);

  return ranges.reduce<Array<null | { start: number; end: number }>>((acc, leg, index) => {
    if (!/^\d+-\d+$/.test(leg)) {
      acc[index] = null;
      return acc;
    }

    const parsedLeg = leg.split(RANGE_DELIMITER);
    acc[index] = {
      start: parseInt(parsedLeg[0], 10),
      end: parseInt(parsedLeg[1], 10),
    };
    return acc;
  }, []);
};

const serializeInteger = (input: Maybe<Number>) =>
  input && /^\d+$/.test(input.toString()) ? input.toString() : null;

const deserializeInteger = (qp: Maybe<string>) =>
  qp && /^\d+$/.test(qp) ? parseInt(qp, 10) : null;

const serializeBoolean = (input: Maybe<boolean>) =>
  input && /true/i.test(input.toString()) ? input.toString() : null;

const deserializeBoolean = (qp: Maybe<string>) =>
  qp && /true/i.test(qp) ? qp.toLowerCase() === 'true' : null;

export const integerRangesParamParser: QParamParser<
  Array<
    | {
        start: number;
        end: number;
      }
    | null
    | undefined
  >,
  string | null | undefined
> = {
  serialize: serializeIntegerRanges,
  deserialize: deserializeIntegerRanges,
};

export const booleanKeyValuesParamParser: QParamParser<
  Array<{
    key: string;
    value: boolean;
  }>,
  string | null | undefined
> = {
  serialize: serializeBooleanKeyValuesQueryParam,
  deserialize: deserializeBooleanKeyValues,
};
export const booleanParamParser: QParamParser<Nullable<boolean>, string | null | undefined> = {
  serialize: serializeBoolean,
  deserialize: deserializeBoolean,
};
export const integerParamParser: QParamParser<Nullable<number>, string | null | undefined> = {
  serialize: serializeInteger,
  deserialize: deserializeInteger,
};

export const updateURLSearchParams = (
  search: string,
  params: Record<string, Nullable<string>>,
): string => {
  const searchParams = new URLSearchParams(search);

  Object.entries(params).forEach(([key, value]) => {
    searchParams.delete(key);
    if (value !== null) {
      searchParams.set(key, value);
    }
  });

  return decodeURIComponent(searchParams.toString());
};
